import shippedSkupPiles from "./skupPileShipped.json" with { type: "json" };

/**
 * Authored bag heaps for a given absence. Admin drags bags on the lot,
 * saves the poses as N hours, and later dumps use that arrangement.
 * Empty defaults fall back to the procedural mound.
 */
export interface SkupBagPose {
  x: number;
  y: number;
  w: number;
  h: number;
  rot: number;
}

export interface SkupPileLayout {
  hours: number;
  poses: SkupBagPose[];
  /** Bag width as a fraction of the butelkomat. Captured when the heap is saved. */
  bagSize?: number;
  /** Pixels from the bottom of the field to the lot. Higher sits the heap further up. */
  lotInset?: number;
}

export interface SkupPileTune {
  bagSize: number;
  lotInset: number;
}

export const DEFAULT_SKUP_PILE_TUNE: SkupPileTune = {
  bagSize: 0.55,
  lotInset: 71,
};

export const SKUP_BAG_SIZE_MIN = 0.28;
export const SKUP_BAG_SIZE_MAX = 0.85;
export const SKUP_LOT_INSET_MIN = 8;
export const SKUP_LOT_INSET_MAX = 220;

const HOUR_MS = 60 * 60 * 1000;

export function hoursFromMs(ms: number): number {
  return Math.max(0, ms / HOUR_MS);
}

export function roundSkupHours(hours: number): number {
  if (!Number.isFinite(hours)) return 1;
  return Math.max(1, Math.min(8, Math.round(hours)));
}

export function parseSkupPileLayouts(raw: unknown): SkupPileLayout[] {
  const list = Array.isArray(raw) ? raw : [];
  const layouts: SkupPileLayout[] = [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const hours = roundSkupHours(Number(rec.hours));
    const poses = parsePoses(rec.poses);
    if (poses.length === 0) continue;
    const bagSize = Number(rec.bagSize);
    const lotInset = Number(rec.lotInset);
    const layout: SkupPileLayout = { hours, poses };
    if (Number.isFinite(bagSize)) layout.bagSize = clampSkupBagSize(bagSize);
    if (Number.isFinite(lotInset)) layout.lotInset = clampSkupLotInset(lotInset);
    layouts.push(layout);
  }
  return mergeSkupPileLayouts([], layouts);
}

/** Shipped snapshots. Admin JSON from the pile editor lands here. */
export const DEFAULT_SKUP_PILE_LAYOUTS: SkupPileLayout[] = parseSkupPileLayouts(
  (shippedSkupPiles as { layouts: unknown }).layouts,
);

export function clampSkupBagSize(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SKUP_PILE_TUNE.bagSize;
  return Math.min(SKUP_BAG_SIZE_MAX, Math.max(SKUP_BAG_SIZE_MIN, value));
}

export function clampSkupLotInset(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SKUP_PILE_TUNE.lotInset;
  return Math.min(SKUP_LOT_INSET_MAX, Math.max(SKUP_LOT_INSET_MIN, Math.round(value)));
}

export function parseSkupPileTune(raw: unknown): SkupPileTune {
  const src = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    bagSize: clampSkupBagSize(Number(src.bagSize)),
    lotInset: clampSkupLotInset(Number(src.lotInset)),
  };
}

export function parseSkupPileStore(raw: unknown): { tune: SkupPileTune; layouts: SkupPileLayout[] } {
  if (Array.isArray(raw)) {
    return { tune: { ...DEFAULT_SKUP_PILE_TUNE }, layouts: parseSkupPileLayouts(raw) };
  }
  if (raw && typeof raw === "object") {
    const rec = raw as Record<string, unknown>;
    return {
      tune: parseSkupPileTune(rec.tune ?? rec),
      layouts: parseSkupPileLayouts(rec.layouts ?? raw),
    };
  }
  return { tune: { ...DEFAULT_SKUP_PILE_TUNE }, layouts: [] };
}

export function tuneFromLayout(layout: SkupPileLayout | null | undefined): SkupPileTune {
  return {
    bagSize: layout?.bagSize != null ? clampSkupBagSize(layout.bagSize) : DEFAULT_SKUP_PILE_TUNE.bagSize,
    lotInset: layout?.lotInset != null ? clampSkupLotInset(layout.lotInset) : DEFAULT_SKUP_PILE_TUNE.lotInset,
  };
}

/** Scale a saved pose from the size/floor it was authored at onto the live knobs. */
export function applySkupPileTune(
  pose: SkupBagPose,
  from: SkupPileTune,
  to: SkupPileTune,
): SkupBagPose {
  const ratio = to.bagSize / Math.max(0.05, from.bagSize);
  const dy = from.lotInset - to.lotInset;
  if (Math.abs(ratio - 1) < 0.001 && dy === 0) return pose;
  const cx = pose.x + pose.w * 0.5;
  const bottom = pose.y + pose.h;
  const w = Math.max(8, pose.w * ratio);
  const h = Math.max(8, pose.h * ratio);
  return {
    x: cx - w * 0.5,
    y: bottom - h + dy,
    w,
    h,
    rot: pose.rot,
  };
}

function parsePoses(raw: unknown): SkupBagPose[] {
  if (!Array.isArray(raw)) return [];
  const poses: SkupBagPose[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const x = Number(rec.x);
    const y = Number(rec.y);
    const w = Number(rec.w);
    const h = Number(rec.h);
    const rot = Number(rec.rot);
    if (![x, y, w, h, rot].every(Number.isFinite)) continue;
    if (w < 8 || h < 8) continue;
    poses.push({ x, y, w, h, rot });
  }
  return poses;
}

/** Local snapshots override shipped ones with the same hour key. */
export function mergeSkupPileLayouts(
  base: readonly SkupPileLayout[],
  overlay: readonly SkupPileLayout[],
): SkupPileLayout[] {
  const byHours = new Map<number, SkupPileLayout>();
  for (const layout of base) byHours.set(layout.hours, layout);
  for (const layout of overlay) byHours.set(layout.hours, layout);
  return [...byHours.values()].sort((a, b) => a.hours - b.hours);
}

export function upsertSkupPileLayout(
  layouts: readonly SkupPileLayout[],
  next: SkupPileLayout,
): SkupPileLayout[] {
  return mergeSkupPileLayouts(layouts, [next]);
}

export function layoutForHours(
  layouts: readonly SkupPileLayout[],
  hours: number,
): SkupPileLayout | null {
  if (layouts.length === 0) return null;
  const target = Number.isFinite(hours) ? hours : 0;
  const rounded = roundSkupHours(target);
  const exact = layouts.find((layout) => layout.hours === rounded);
  if (exact) return exact;
  let best = layouts[0]!;
  for (const layout of layouts) {
    if (Math.abs(layout.hours - target) < Math.abs(best.hours - target)) best = layout;
  }
  return best;
}

export function poseForBag(
  layouts: readonly SkupPileLayout[],
  hours: number,
  bagId: number,
): SkupBagPose | null {
  const layout = layoutForHours(layouts, hours);
  if (!layout) return null;
  return layout.poses[bagId - 1] ?? null;
}

/**
 * Side of the mound: -1 left edge, 0 middle, +1 right edge.
 * CSS degrees, clockwise-positive. Left bags lean right, right bags lean left.
 */
export function skupBagLeanDeg(side: number): number {
  const t = Math.max(-1, Math.min(1, side));
  const dead = 0.2;
  const mag = Math.abs(t);
  if (mag <= dead) return 0;
  const u = (mag - dead) / (1 - dead);
  return -Math.sign(t) * u * u * 20;
}
